import { PrivacyPolicyStructuredData } from '@/components/seo/PrivacyPolicyStructuredData';
import { LegalLayout, type LegalSection } from '@/components/legal/LegalLayout';
import { useSEO } from '@/hooks/useSEO';

const SECTIONS: LegalSection[] = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'information-we-collect', label: 'Information We Collect' },
  { id: 'how-we-use', label: 'How We Use Your Information' },
  { id: 'data-sharing', label: 'Data Sharing and Disclosure' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'cookies', label: 'Cookies and Tracking Technologies' },
  { id: 'international-transfers', label: 'International Data Transfers' },
  { id: 'children', label: "Children's Privacy" },
  { id: 'changes', label: 'Changes to This Policy' },
  { id: 'contact', label: 'Contact Us' },
];

export default function PrivacyPolicy() {
  useSEO({
    title: 'Privacy Policy | Beatview',
    description: 'Learn how Beatview protects your data and respects your privacy. Our comprehensive privacy policy covers data collection, usage, and your rights under GDPR and CCPA.',
    canonicalUrl: 'https://beatview.ai/privacy-policy',
  });

  return (
    <LegalLayout
      title={<>Privacy <span className="bv-hero-highlight">Policy</span></>}
      updated="September 19, 2026"
      summary="How Beatview collects, uses and protects personal data about customers and candidates, how long we keep it, and the rights you have under GDPR and CCPA."
      current="/privacy-policy"
      sections={SECTIONS}
      contact={{ label: 'Questions about your data?', email: 'privacy@beatview.ai' }}
    >
      <PrivacyPolicyStructuredData />
          <section id="introduction">
            <h2>1. Introduction</h2>
            <p>
              Beatview ("we," "our," or "us") is an AI-powered talent intelligence platform designed to help 
              enterprise teams hire smarter, faster, and with more confidence. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our platform, 
              including our website, applications, and services.
            </p>
            <p>
              We are committed to protecting your privacy and handling your data in an open and transparent 
              manner. This policy applies to all users of our platform, including employers, recruiters, 
              hiring managers, and job candidates.
            </p>
          </section>
          <section id="information-we-collect">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide</h3>
            <p><strong>Account Information:</strong> When you create an account, we collect your name, email address, company name, and password.</p>
            <p><strong>Profile Information:</strong> Additional details you provide about yourself or your organization, such as job titles, company size, and industry.</p>

            <h3>2.2 Candidate Data</h3>
            <p>When candidates apply for positions through our platform, we collect:</p>
            <ul>
              <li>Resume/CV files and their parsed content (work history, education, skills)</li>
              <li>Contact information (name, email, phone number, location)</li>
              <li>LinkedIn profile URLs and other professional links</li>
              <li>Responses to screening questions</li>
              <li>Video interview recordings and AI-generated assessments of each answer (Beatview does not store word-for-word transcripts)</li>
              <li>Work style assessment responses and computed personality insights</li>
            </ul>

            <h3>2.3 Automatically Collected Information</h3>
            <p>We automatically collect certain information when you use our platform:</p>
            <ul>
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>Log data (IP address, access times, pages viewed)</li>
              <li>Usage patterns and feature interactions</li>
              <li>Geographic location (derived from IP address)</li>
            </ul>
          </section>
          <section id="how-we-use">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>

            <h3>3.1 Platform Operations</h3>
            <ul>
              <li>Providing and maintaining our recruitment platform services</li>
              <li>Processing job applications and managing candidate pipelines</li>
              <li>Enabling communication between employers and candidates</li>
              <li>Hosting and displaying career pages</li>
            </ul>

            <h3>3.2 AI-Powered Analysis</h3>
            <ul>
              <li>Parsing and analyzing resumes to extract relevant information</li>
              <li>Generating AI interview questions tailored to job requirements</li>
              <li>Scoring interview responses and providing feedback insights</li>
              <li>Computing work style assessments and cultural fit indicators</li>
              <li>Ranking candidates based on job match scores</li>
              <li>Creating interview preparation reports for hiring managers</li>
            </ul>

            <h3>3.3 AI Training and Data Usage</h3>
            <p>
              <strong>Beatview does not use customer or candidate data to train shared or public AI models 
              without explicit authorization.</strong> Your data is processed solely to provide services to 
              you and is not used to improve models that serve other customers.
            </p>
            <p>
              Any use of anonymized or aggregated data for improving our AI systems requires explicit consent 
              and is governed by our Data Processing Agreement. You maintain full control over whether your 
              data participates in any model training programs.
            </p>

            <h3>3.4 Communications</h3>
            <ul>
              <li>Sending interview invitations and application confirmations</li>
              <li>Notifying employers of new applications</li>
              <li>Providing platform updates and feature announcements</li>
              <li>Responding to support inquiries</li>
            </ul>
          </section>
          <section id="data-sharing">
            <h2>4. Data Sharing and Disclosure</h2>
            <p>We may share your information in the following circumstances:</p>

            <h3>4.1 With Employers</h3>
            <p>
              Candidate information is shared with the employers to whom they have applied. This includes 
              resume data, interview recordings, assessment results, and AI-generated insights.
            </p>

            <h3>4.2 Service Providers</h3>
            <p>
              We work with third-party service providers who assist us in operating our platform, including:
            </p>
            <ul>
              <li>Cloud hosting and infrastructure providers (Supabase, AWS)</li>
              <li>AI and machine learning services for resume parsing and analysis</li>
              <li>Video recording and storage services</li>
              <li>Email delivery services</li>
              <li>Analytics providers</li>
            </ul>

            <h3>4.3 Legal Requirements</h3>
            <p>
              We may disclose information if required by law, subpoena, or other legal process, or if we 
              believe disclosure is necessary to protect our rights, your safety, or the safety of others.
            </p>

            <h3>4.4 Third-Party Integrations</h3>
            <p>
              If you choose to connect third-party services (such as LinkedIn or Zapier), we may share 
              data with those services as directed by you.
            </p>

            <h3>4.5 AI Assistants and Agents (MCP Server)</h3>
            <p>
              Beatview offers a Model Context Protocol (MCP) server that lets you connect AI assistants and 
              agents you choose, such as Claude, ChatGPT, Gemini or Cursor, to your Beatview account. When you 
              authorize a connection, we send the assistant the information it requests on your behalf. This 
              can include job details, candidate names and locations, screening answers, resume insights, AI 
              scores, interview evaluations, work style results and recruiter notes. Once sent, that 
              information is processed by the provider of the assistant under your agreement with that 
              provider and its privacy policy, not this policy.
            </p>
            <p>We limit what connected assistants receive:</p>
            <ul>
              <li>Video interview recordings are never sent to a connected assistant. It receives a link that opens the recording inside Beatview for signed-in members of your account.</li>
              <li>Resume files are shared only as temporary download links that expire after one hour.</li>
              <li>Each connection can access only the Beatview account that authorized it, and you can revoke it at any time in Integrations → MCP Server → Settings.</li>
            </ul>
            <p>
              If you connect an assistant, you are responsible for making sure the provider is approved by 
              your organization for handling candidate data, that candidates have received any notice your 
              use requires, and that your use complies with applicable law. For candidate data shared through 
              a connection, Beatview acts on your instructions.
            </p>
          </section>
          <section id="data-retention">
            <h2>5. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to fulfill the purposes outlined in this 
              policy, unless a longer retention period is required or permitted by law.
            </p>
            <ul>
              <li><strong>Account data:</strong> Retained while your account is active and for up to 3 years after deletion</li>
              <li><strong>Candidate applications:</strong> Retained for up to 2 years after the position is filled or closed</li>
              <li><strong>Video interview recordings:</strong> Retained for up to 1 year after the interview</li>
              <li><strong>Work style assessments:</strong> Retained for the duration of the application process plus 1 year</li>
              <li><strong>Usage logs:</strong> Retained for up to 90 days for security and debugging purposes</li>
            </ul>
          </section>
          <section id="your-rights">
            <h2>6. Your Rights</h2>
            <p>
              Depending on your location, you may have the following rights regarding your personal data:
            </p>

            <h3>6.1 GDPR Rights (European Users)</h3>
            <ul>
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of data processing</li>
              <li><strong>Right to Data Portability:</strong> Request your data in a portable format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>

            <h3>6.2 CCPA Rights (California Residents)</h3>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to know whether personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to request deletion of personal information</li>
              <li>Right to non-discrimination for exercising these rights</li>
            </ul>

            <p>
              To exercise any of these rights, please contact us at privacy@beatview.ai.
            </p>
          </section>
          <section id="cookies">
            <h2>7. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience on our platform:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic platform functionality and authentication</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p>
              You can control cookie preferences through your browser settings. Note that disabling certain 
              cookies may affect platform functionality.
            </p>
          </section>
          <section id="international-transfers">
            <h2>8. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of 
              residence. These countries may have different data protection laws.
            </p>
            <p>
              When we transfer data internationally, we implement appropriate safeguards, including:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>Data processing agreements with our service providers</li>
              <li>Technical security measures to protect data in transit</li>
            </ul>
          </section>
          <section id="children">
            <h2>9. Children's Privacy</h2>
            <p>
              Our platform is not intended for individuals under the age of 16. We do not knowingly collect 
              personal information from children. If we become aware that we have collected data from a 
              child without parental consent, we will take steps to delete that information.
            </p>
          </section>
          <section id="changes">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new policy on this page and updating the "Last updated" date. We 
              encourage you to review this policy periodically.
            </p>
          </section>
          <section id="contact">
            <h2>11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul>
              <li><strong>Email:</strong> privacy@beatview.ai</li>
              <li><strong>Data Protection Officer:</strong> dpo@beatview.ai</li>
            </ul>
            <p>
              For GDPR-related inquiries, you also have the right to lodge a complaint with your local 
              data protection authority.
            </p>
          </section>
    </LegalLayout>
  );
}
